/**
 * React Client: Calling Alpha Comparison API via WebSocket
 *
 * This example shows how to call the WebSocket endpoint from a React client
 * using the browser's native WebSocket API.
 */

import { useState } from 'react';
import { Button } from '@novaquantis/design-system';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ChartInstance from './ChartInstance';
import type { RequestData } from './types';

// Re-export MESSAGES for backwards compatibility
export { MESSAGES } from './constants';

// Default request data for new chart instances
const defaultRequestData = {
  start_date: '2020-01-01',
  period: '4years',
  investment: 100000,
  rebalance_frequency_days: 21,
  benchmark: "SP500",
  method: "alpha_weighted",
  no_cache: true,
};

interface ChartInstanceData {
  id: number;
  requestData: RequestData;
}

function Simulator() {
  const [chartInstances, setChartInstances] = useState<ChartInstanceData[]>([]);
  const [pendingCharts, setPendingCharts] = useState<ChartInstanceData[]>([]);
  const [nextChartId, setNextChartId] = useState(1);
  const [defaultExpanded, setDefaultExpanded] = useState(true);

  const createNewChart = () => {
    const newChartId = nextChartId;
    setNextChartId((prev) => prev + 1);

    setPendingCharts((prev) => [
      {
        id: newChartId,
        requestData: { ...defaultRequestData },
      },
      ...prev,
    ]);
  };

  const startChart = (pendingChartId: number) => {
    const pendingChart = pendingCharts.find(
      (chart) => chart.id === pendingChartId
    );
    if (!pendingChart) return;

    // Move from pending to active
    setPendingCharts((prev) =>
      prev.filter((chart) => chart.id !== pendingChartId)
    );
    setChartInstances((prev) => [pendingChart, ...prev]);
  };

  const removePendingChart = (chartId: number) => {
    setPendingCharts((prev) => prev.filter((chart) => chart.id !== chartId));
  };

  const removeChart = (chartId: number) => {
    setChartInstances((prev) => prev.filter((chart) => chart.id !== chartId));
  };

  const updatePendingChartData = (
    chartId: number,
    field: keyof RequestData,
    value: string | number | boolean
  ) => {
    setPendingCharts((prev) =>
      prev.map((chart) =>
        chart.id === chartId
          ? {
              ...chart,
              requestData: {
                ...chart.requestData,
                [field]: value,
              },
            }
          : chart
      )
    );
  };

  return (
    <div className="mx-auto max-w-[1440px] rounded-xl border border-border bg-card p-8 shadow-[var(--shadow-card-soft)]">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="m-0 font-display text-xl font-semibold text-foreground">
          Alpha Comparison
        </h1>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={defaultExpanded}
              onChange={(e) => setDefaultExpanded(e.target.checked)}
            />
            Default to expanded
          </label>
          <Button onClick={createNewChart} variant="glow">
            + Create New Simulation
          </Button>
        </div>
      </div>

      {pendingCharts.map((pendingChart) => (
        <div
          key={pendingChart.id}
          className="mb-5 rounded-[10px] border border-[color:var(--nq-line-soft)] bg-muted p-5"
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="m-0 font-display text-base text-foreground">
              Setup:{' '}
              {pendingChart.requestData.period
                .replace('years', ' years')
                .replace('year', ' year')}
              , starting on {pendingChart.requestData.start_date}
            </h2>
            <Button
              variant="ghost"
              onClick={() => removePendingChart(pendingChart.id)}
            >
              Cancel
            </Button>
          </div>

          <div className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`start-date-${pendingChart.id}`}>Start Date</Label>
              <Input
                id={`start-date-${pendingChart.id}`}
                type="date"
                value={pendingChart.requestData.start_date}
                onChange={(e) =>
                  updatePendingChartData(
                    pendingChart.id,
                    'start_date',
                    e.target.value,
                  )
                }
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`period-${pendingChart.id}`}>Period</Label>
              <Select
                value={pendingChart.requestData.period}
                onValueChange={(value) =>
                  updatePendingChartData(pendingChart.id, 'period', value)
                }
              >
                <SelectTrigger id={`period-${pendingChart.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1year">1 Year</SelectItem>
                  <SelectItem value="2years">2 Years</SelectItem>
                  <SelectItem value="3years">3 Years</SelectItem>
                  <SelectItem value="4years">4 Years</SelectItem>
                  <SelectItem value="5years">5 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`investment-${pendingChart.id}`}>Investment Amount</Label>
              <Input
                id={`investment-${pendingChart.id}`}
                type="number"
                value={pendingChart.requestData.investment}
                onChange={(e) =>
                  updatePendingChartData(
                    pendingChart.id,
                    'investment',
                    parseInt(e.target.value, 10),
                  )
                }
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={() => startChart(pendingChart.id)} variant="glow">
              Start
            </Button>
          </div>
        </div>
      ))}

      {chartInstances.length === 0 && pendingCharts.length === 0 && (
        <div className="mb-5 rounded-[10px] border border-dashed border-[color:var(--nq-line-soft)] bg-muted p-5 text-center">
          <p className="m-0 mb-3 font-display text-base text-foreground">
            No simulations created yet.
          </p>
          <p className="m-0 text-sm text-muted-foreground">
            Click &quot;Create New Simulation&quot; to start a new simulation analysis.
          </p>
        </div>
      )}

      {chartInstances.map((chart) => (
        <ChartInstance
          key={chart.id}
          chartId={chart.id}
          requestData={chart.requestData}
          defaultExpanded={defaultExpanded}
          onRemove={() => removeChart(chart.id)}
        />
      ))}
    </div>
  );
}

export default Simulator;
